'use client';

import { useCallback } from 'react';
import {
  BookingJsonldReadBookingState,
  useApiBookingsGetCollection,
} from '@ae/data-access';
import {
  Locale,
  useDate,
  useGetProdBeDomain,
  useGetProdDomain,
  useLocale,
  useTranslation,
} from '@ae/shared';
import { LinkGetter, useLink, useMe } from '../hooks';

export enum AELink {
  AllStaysBe,
  AllStaysNl,
  Home,
  About,
  BecomeOwner,
  Blog,
  BlogOwner,
  Contact,
  HelperCenterContact,
  Conditions,
  ConfidentialPolicy,
  FAQ,
  GeneralRegulation,
  GiftCard,
  HelpCenter,
  HelpCenterOwner,
  Jobs,
  Insurance,
  Dsa,
  InsuranceCancellation,
  InsuranceDamage,
  Owner,
  Search,
  VoucherFaq,
  VoucherOrder,
  Nature,
  Cycling,
  Fishing,
  Relaxation,
  Baby,
  Children,
  BusinessTrip,
  UnforgettableExperiences,
  WalkingTrail,
  Chalets,
  Castles,
  LuxuryVillas,
  UnusualAccommodations,
  CharmingCottage,
  IndoorPool,
  OutdoorPool,
  Swimmingpool,
  HotTub,
  Sauna,
  Inspiration,
  RegionsArdennes,
  Durbuy,
  Malmedy,
  LaRoche,
  Houffalize,
  EauDheure,
  Dinant,
  Namur,
  Bouillon,
  Spa,
  New,
  Summer,
  Wellness,
  Fireplace,
  TerraceBreathtakingView,
  MayCampagne,
  DurbuyThematic,
  RomanticWeekend,
  PrivacyPolicy,
  Disclaimer,
  Ascension,
  PrivateGarden,
  EntertainmentStay,
  CampagneActionPromo,
}

export const useGetAELink = () => {
  const locale = useLocale();
  const { dayjs } = useDate();
  const { t } = useTranslation('footer', 'home');
  const getProdBeDomain = useGetProdBeDomain();
  const getProdDomain = useGetProdDomain();
  const { authenticated } = useMe();
  const { data: bookings } = useApiBookingsGetCollection({
    query: { enabled: authenticated },
  });
  const prodDomain = getProdDomain(locale);

  const getLink = useCallback(
    (link: AELink): LinkGetter => {
      switch (link) {
        case AELink.Home:
          return { href: `${prodDomain}` };
        case AELink.About:
          return {
            href: () => `${prodDomain}/made-in-ardenne`,
            title: t('footer.about'),
            alt: t('footer.about'),
          };
        case AELink.BecomeOwner:
          return {
            href: `${prodDomain}/newlead.do`,
            title: t('footer.become_owner'),
            alt: t('footer.become_owner'),
          };
        case AELink.Blog:
          return {
            href: () => {
              switch (locale) {
                case Locale.Nl:
                  return `${getProdDomain(Locale.Nl)}/experience/blog`;
                case Locale.Fr:
                  return `${getProdDomain(Locale.Fr)}/experience/blog`;
                case Locale.De:
                case Locale.En:
                default:
                  return `${getProdDomain(Locale.En)}/experience/blog`;
              }
            },
            target: '_blank',
          };
        case AELink.BlogOwner:
          return {
            href: () => {
              switch (locale) {
                case Locale.Nl:
                case Locale.De:
                  return `${getProdBeDomain(Locale.Nl)}/blog-gite`;
                case Locale.Fr:
                case Locale.En:
                default:
                  return `${getProdBeDomain(Locale.Fr)}/blog-gite`;
              }
            },
            title: t('footer.i_run_my_holiday_home'),
            alt: t('footer.i_run_my_holiday_home'),
            target: '_blank',
          };
        case AELink.Contact:
          return {
            href: `${getProdDomain(locale)}/contact`,
            title: t('footer.contact_us'),
            alt: t('footer.contact_us'),
          };
        case AELink.HelperCenterContact:
          return {
            href: `${getProdDomain(locale)}/help-center#helpcenterContact`,
            title: t('footer.contact_us'),
            alt: t('footer.contact_us'),
          };
        case AELink.Conditions:
          return {
            href: () => {
              switch (locale) {
                case Locale.Fr:
                  return `${getProdDomain(Locale.Fr)}/conditions-generales`;
                case Locale.Nl:
                  return `${getProdDomain(Locale.Nl)}/algemene-voorwaarden`;
                case Locale.En:
                  return `${getProdDomain(Locale.En)}/general-conditions`;
                case Locale.De:
                  return `${getProdDomain(Locale.De)}/allgemeine-bedingungen`;
                default:
                  return '';
              }
            },
            title: t('footer.general_conditions'),
            alt: t('footer.general_conditions'),
          };
        case AELink.AllStaysBe:
          return {
            href: () => {
              switch (locale) {
                case Locale.Fr:
                  return `https://bit.ly/3Nhny1A`;
                case Locale.Nl:
                  return `https://bit.ly/3Ng5TqN`;
                case Locale.En:
                case Locale.De:
                default:
                  return '';
              }
            },
          };
        case AELink.AllStaysNl:
          return {
            href: () => {
              switch (locale) {
                case Locale.Fr:
                case Locale.Nl:
                  return `https://bit.ly/3zO6SeM`;
                case Locale.En:
                case Locale.De:
                default:
                  return '';
              }
            },
          };
        case AELink.ConfidentialPolicy:
          return {
            href: `${getProdDomain(locale)}/privacy-policy`,
            title: t('footer.privacy_policy'),
            alt: t('footer.privacy_policy'),
          };

        case AELink.GiftCard:
          return {
            href: () => {
              switch (locale) {
                case Locale.Fr:
                  return `${getProdDomain(Locale.Fr)}/cheque-cadeau`;
                case Locale.Nl:
                  return `${getProdDomain(Locale.Nl)}/cadeaubon-bestellen`;
                case Locale.De:
                  return `${getProdDomain(Locale.De)}/geschenkgutschein-bestellen`;
                case Locale.En:
                  return `${getProdDomain(Locale.En)}/gift-vouchers`;
                default:
                  return '';
              }
            },
            title: t('footer.offer_a_gift'),
            alt: t('footer.offer_a_gift'),
            trackingname: 'order_voucher',
          };
        case AELink.HelpCenter:
          return {
            href: () => {
              const hasFutureBooking = bookings?.['hydra:member']?.some(
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                (b: any) =>
                  b.state !== BookingJsonldReadBookingState.canceled &&
                  dayjs(b.endDate).isAfter(dayjs())
              );

              const url = `${getProdDomain(locale)}/help-center`;

              if (!authenticated) {
                return url;
              }

              switch (locale) {
                case Locale.Fr:
                  return hasFutureBooking
                    ? `${url}/2-ma-reservation`
                    : `${url}/1-recherche-d'un-sejour`;

                case Locale.Nl:
                  return hasFutureBooking
                    ? `${url}/2-mijn-reservatie`
                    : `${url}/1-op-zoek-naar-een-verblijf`;

                case Locale.De:
                  return hasFutureBooking
                    ? `${url}/2-meine-buchung`
                    : `${url}/1-suche-nach-einem-aufenthalt`;

                case Locale.En:
                default:
                  return hasFutureBooking
                    ? `${url}/2-manage-my-booking`
                    : `${url}/1-looking-for-a-stay`;
              }
            },
          };
        case AELink.HelpCenterOwner:
          return {
            href: `${getProdDomain(locale)}/help-center-proprietaire`,
            title: t('footer.help-center-proprietaire'),
            alt: t('footer.help-center-proprietaire'),
          };
        case AELink.FAQ:
          return {
            href: `${getProdDomain(locale)}/help-center`,
            title: t('footer.faq'),
            alt: t('footer.faq'),
          };
        case AELink.Jobs:
          return {
            href: `${getProdDomain(locale)}/jobs`,
            title: t('footer.jobs'),
            alt: t('footer.jobs'),
          };
        case AELink.Dsa:
          return {
            href: `${getProdDomain(locale)}/dsa`,
            title: t('footer.dsa'),
            alt: t('footer.dsa'),
          };
        case AELink.InsuranceCancellation:
        case AELink.Insurance:
          return {
            href: () => {
              switch (locale) {
                case Locale.Fr:
                  return `${getProdDomain(Locale.Fr)}/assurances`;
                case Locale.Nl:
                  return `${getProdDomain(Locale.Nl)}/verzekeringen`;
                case Locale.De:
                  return `${getProdDomain(Locale.De)}/versicherungen`;
                case Locale.En:
                  return `${getProdDomain(Locale.En)}/insurance`;
                default:
                  return '';
              }
            },
            title: t('footer.insurance'),
            alt: t('footer.insurance'),
          };
        case AELink.Nature:
          return {
            href: () => {
              switch (locale) {
                case Locale.Fr:
                  return `${getProdDomain(Locale.Fr)}/experience/decouvrez-la-nature-intacte-de-2-parcs-nationaux-en-ardenne`;
                case Locale.Nl:
                  return `${getProdDomain(Locale.Nl)}/experience/ontdek-de-ongerepte-natuur-in-2-nationale-parken-in-de-ardennen`;
                case Locale.De:
                  return `${getProdDomain(Locale.De)}/experience/discover-the-unspoilt-nature-of-two-ardennes-national-parks`;
                case Locale.En:
                  return `${getProdDomain(Locale.En)}/experience/discover-the-unspoilt-nature-of-two-ardennes-national-parks`;
                default:
                  return '';
              }
            },
          };
        case AELink.Cycling:
          return {
            href: () => {
              switch (locale) {
                case Locale.Fr:
                  return `${getProdDomain(Locale.Fr)}/experience/top-12-gites-velo-friendly-en-ardenne`;
                case Locale.Nl:
                  return `${getProdDomain(Locale.Nl)}/experience/news/top-12-fietsvriendelijke-vakantiehuizen-de-ardennen`;
                case Locale.De:
                  return `${getProdDomain(Locale.De)}/experience/top-12-bike-friendly-holiday-homes-in-the-ardennes`;
                case Locale.En:
                  return `${getProdDomain(Locale.En)}/experience/top-12-bike-friendly-holiday-homes-in-the-ardennes`;
                default:
                  return '';
              }
            },
          };

        case AELink.Fishing:
          return {
            href: () => {
              switch (locale) {
                case Locale.Fr:
                  return `${getProdDomain(Locale.Fr)}/maisons-de-vacances-ardennes/pecher-en-ardenne`;
                case Locale.Nl:
                  return `${getProdDomain(Locale.Nl)}/vakantiehuizen-ardennen/vissen-in-de-ardennen`;
                case Locale.De:
                  return `${getProdDomain(Locale.De)}/ferienhauser-ardennen/angeln-in-den-ardennen`;
                case Locale.En:
                  return `${getProdDomain(Locale.En)}/holiday-houses-ardennes/fishing-in-the-ardennes`;
                default:
                  return '';
              }
            },
          };

        case AELink.Baby:
          return {
            href: () => {
              switch (locale) {
                case Locale.Fr:
                  return `${getProdDomain(Locale.Fr)}/maisons-de-vacances-ardennes/sejour-avec-bebe`;
                case Locale.Nl:
                  return `${getProdDomain(Locale.Nl)}/vakantiehuizen-ardennen/verblijf-met-baby`;
                case Locale.De:
                  return `${getProdDomain(Locale.De)}/ferienhauser-ardennen/aufenthalt-mit-baby`;
                case Locale.En:
                  return `${getProdDomain(Locale.En)}/holiday-houses-ardennes/stay-with-baby`;
                default:
                  return '';
              }
            },
          };

        case AELink.Relaxation:
          return {
            href: () => {
              switch (locale) {
                case Locale.Fr:
                  return `${getProdDomain(Locale.Fr)}/maisons-de-vacances-ardennes/se-ressourcer-en-ardenne`;
                case Locale.Nl:
                  return `${getProdDomain(Locale.Nl)}/vakantiehuizen-ardennen/onthaasten-in-de-ardennen`;
                case Locale.De:
                  return `${getProdDomain(Locale.De)}/ferienhauser-ardennen/entschleunigen-in-den-ardennen`;
                case Locale.En:
                  return `${getProdDomain(Locale.En)}/holiday-houses-ardennes/relaxing-in-the-ardennes`;
                default:
                  return '';
              }
            },
          };

        case AELink.Children:
          return {
            href: () => {
              switch (locale) {
                case Locale.Fr:
                  return `${getProdDomain(Locale.Fr)}/maisons-de-vacances-ardennes/sejour-avec-enfants`;
                case Locale.Nl:
                  return `${getProdDomain(Locale.Nl)}/vakantiehuizen-ardennen/verblijf-met-kinderen`;
                case Locale.De:
                  return `${getProdDomain(Locale.De)}/ferienhauser-ardennen/aufenthalt-mit-kindern`;
                case Locale.En:
                  return `${getProdDomain(Locale.En)}/holiday-houses-ardennes/stay-with-kids`;
                default:
                  return '';
              }
            },
          };

        case AELink.BusinessTrip:
          return {
            href: () => {
              switch (locale) {
                case Locale.Fr:
                  return `${getProdDomain(Locale.Fr)}/seminaire-gite-affaires-ardenne`;
                case Locale.Nl:
                  return `${getProdDomain(Locale.Nl)}/seminarie-workation-business-ardennen`;
                case Locale.De:
                  return `${getProdDomain(Locale.De)}/suchen/1-99p/MidWeek/0000-00-00/business-aufenthalt?TriRecherche=Satisfaction-DESC`;
                case Locale.En:
                  return `${getProdDomain(Locale.En)}/search/1-99p/MidWeek/0000-00-00/business-trip/?TriRecherche=Satisfaction-DESC`;
                default:
                  return '';
              }
            },
          };

        case AELink.WalkingTrail:
          return {
            href: () => {
              switch (locale) {
                case Locale.Fr:
                  return `${getProdDomain(Locale.Fr)}/experience/balades?cities=all&field_type=All`;
                case Locale.Nl:
                  return `${getProdDomain(Locale.Nl)}/experience/wandelingen?cities=all&field_type=All`;
                case Locale.De:
                  return `${getProdDomain(Locale.De)}/experience/hikes?cities=all&field_type=All`;
                case Locale.En:
                  return `${getProdDomain(Locale.En)}/experience/hikes?cities=all&field_type=All`;
                default:
                  return '';
              }
            },
          };

        case AELink.UnforgettableExperiences:
          return {
            href: () => {
              switch (locale) {
                case Locale.Fr:
                  return `${getProdDomain(Locale.Fr)}/experience/recherche?cities=all&combine=&field_category_target_id=All`;
                case Locale.Nl:
                  return `${getProdDomain(Locale.Nl)}/experience/zoek?cities=all&combine=&field_category_target_id=All`;
                case Locale.De:
                  return `${getProdDomain(Locale.De)}/experience/search?cities=all&combine=&field_category_target_id=All`;
                case Locale.En:
                  return `${getProdDomain(Locale.En)}/experience/search?cities=all&combine=&field_category_target_id=All`;
                default:
                  return '';
              }
            },
          };

        case AELink.Chalets:
          return {
            href: () => {
              switch (locale) {
                case Locale.Fr:
                  return `${getProdDomain(Locale.Fr)}/maisons-de-vacances-ardennes/Chalets`;
                case Locale.Nl:
                  return `${getProdDomain(Locale.Nl)}/vakantiehuizen-ardennen/Chalets`;
                case Locale.De:
                  return `${getProdDomain(Locale.De)}/ferienhauser-ardennen/Chalets`;
                case Locale.En:
                  return `${getProdDomain(Locale.En)}/holiday-houses-ardennes/Chalets`;
                default:
                  return '';
              }
            },
          };
        case AELink.Castles:
          return {
            href: () => {
              switch (locale) {
                case Locale.Fr:
                  return `${getProdDomain(Locale.Fr)}/maisons-de-vacances-ardennes/chateaux`;
                case Locale.Nl:
                  return `${getProdDomain(Locale.Nl)}/vakantiehuizen-ardennen/kastelen`;
                case Locale.De:
                  return `${getProdDomain(Locale.De)}/ferienhauser-ardennen/schlosser`;
                case Locale.En:
                  return `${getProdDomain(Locale.En)}/holiday-houses-ardennes/castles`;
                default:
                  return '';
              }
            },
          };
        case AELink.LuxuryVillas:
          return {
            href: () => {
              switch (locale) {
                case Locale.Fr:
                  return `${getProdDomain(Locale.Fr)}/maisons-de-vacances-ardennes/villas-de-luxe`;
                case Locale.Nl:
                  return `${getProdDomain(Locale.Nl)}/vakantiehuizen-ardennen/luxe-villas`;
                case Locale.De:
                  return `${getProdDomain(Locale.De)}/ferienhauser-ardennen/luxusvillen`;
                case Locale.En:
                  return `${getProdDomain(Locale.En)}/holiday-houses-ardennes/luxury-villas`;
                default:
                  return '';
              }
            },
          };
        case AELink.UnusualAccommodations:
          return {
            href: () => {
              switch (locale) {
                case Locale.Fr:
                  return `${getProdDomain(Locale.Fr)}/maisons-de-vacances-ardennes/week-end-insolite`;
                case Locale.Nl:
                  return `${getProdDomain(Locale.Nl)}/vakantiehuizen-ardennen/origneel-overnachten`;
                case Locale.De:
                  return `${getProdDomain(Locale.De)}/ferienhauser-ardennen/originell-ubernachten`;
                case Locale.En:
                  return `${getProdDomain(Locale.En)}/holiday-houses-ardennes/original-accommodation`;
                default:
                  return '';
              }
            },
          };
        case AELink.CharmingCottage:
          return {
            href: () => {
              switch (locale) {
                case Locale.Fr:
                  return `${getProdDomain(Locale.Fr)}/maisons-de-vacances-ardennes/gites-de-charme`;
                case Locale.Nl:
                  return `${getProdDomain(Locale.Nl)}/vakantiehuizen-ardennen/huisjes`;
                case Locale.De:
                  return `${getProdDomain(Locale.De)}/ferienhauser-ardennen/cottages`;
                case Locale.En:
                  return `${getProdDomain(Locale.En)}/holiday-houses-ardennes/cottages`;
                default:
                  return '';
              }
            },
          };
        case AELink.IndoorPool:
          return {
            href: () => {
              switch (locale) {
                case Locale.Fr:
                  return `${getProdDomain(Locale.Fr)}/maisons-de-vacances-ardennes/piscine-interieure`;
                case Locale.Nl:
                  return `${getProdDomain(Locale.Nl)}/vakantiehuizen-ardennen/binnenzwembad`;
                case Locale.De:
                  return `${getProdDomain(Locale.De)}/ferienhauser-ardennen/schwimmbad-innen`;
                case Locale.En:
                  return `${getProdDomain(Locale.En)}/holiday-houses-ardennes/indoor-pool`;
                default:
                  return '';
              }
            },
          };
        case AELink.Swimmingpool:
          return {
            href: () => {
              switch (locale) {
                case Locale.Fr:
                  return `${getProdDomain(Locale.Fr)}/maisons-de-vacances-ardennes/gites-avec-piscine`;
                case Locale.Nl:
                  return `${getProdDomain(Locale.Nl)}/vakantiehuizen-ardennen/vakantiehuizen-met-zwembad`;
                case Locale.De:
                  return `${getProdDomain(Locale.De)}/ferienhauser-ardennen/ferienhauser-mit-schwimmbad`;
                case Locale.En:
                  return `${getProdDomain(Locale.En)}/holiday-houses-ardennes/holiday-houses-with-swimmingpool`;
                default:
                  return '';
              }
            },
          };

        case AELink.New:
          return {
            href: () => {
              switch (locale) {
                case Locale.Fr:
                  return `${getProdDomain(Locale.Fr)}/maisons-de-vacances-ardennes/nouveautes`;
                case Locale.Nl:
                  return `${getProdDomain(Locale.Nl)}/vakantiehuizen-ardennen/nieuw`;
                case Locale.De:
                  return `${getProdDomain(Locale.De)}/ferienhauser-ardennen/neu`;
                case Locale.En:
                  return `${getProdDomain(Locale.En)}/holiday-houses-ardennes/new`;
                default:
                  return '';
              }
            },

            alt: t('home:home.campagne.title'),
          };
        case AELink.Ascension:
          return {
            href: () => {
              switch (locale) {
                case Locale.Fr:
                  return `${getProdDomain(Locale.Fr)}/recherche/10-99p/Week-end/2025-05-28_to_2025-06-01/369_maisons-de-vacances-en-Ardenne?TriRecherche=Satisfaction-DESC`;
                case Locale.Nl:
                  return `${getProdDomain(Locale.Nl)}/zoeken/10-99p/Weekend/2025-05-28_to_2025-06-01/369_vakantiehuizen-in-de-Ardennen?TriRecherche=Satisfaction-DESC`;
                case Locale.De:
                  return `${getProdDomain(Locale.De)}/suchen/10-99p/Wochenende/2025-05-28_to_2025-06-01/369_ferienwohnungen-in-den-Ardennen/?TriRecherche=Satisfaction-DESC`;
                case Locale.En:
                  return `${getProdDomain(Locale.En)}/search/10-99p/Weekend/2025-05-28_to_2025-06-01/369_holiday-Houses-in-the-Ardennes?TriRecherche=Satisfaction-DESC`;
                default:
                  return '';
              }
            },
          };

        case AELink.PrivateGarden:
          return {
            href: () => {
              switch (locale) {
                case Locale.Fr:
                  return `${getProdDomain(Locale.Fr)}/recherche/1-99p/Week-end/0000-00-00/3_Confort/jardin-prive/barbecue/les-mieux-notees/?TriRecherche=Prix-ASC`;
                case Locale.Nl:
                  return `${getProdDomain(Locale.Nl)}/zoeken/1-99p/Weekend/0000-00-00/3_Comfort/de-best-beoordeelde/privetuin/barbecue/?TriRecherche=Prix-ASC`;
                case Locale.De:
                  return `${getProdDomain(Locale.De)}/suchen/1-99p/Wochenende/0000-00-00/3_Komfort/privater-garten/grill/bestbewerteten/?TriRecherche=Prix-ASC`;
                case Locale.En:
                  return `${getProdDomain(Locale.En)}/search/1-99p/Weekend/0000-00-00/3_Comfort/top-rated/private-garden/barbecue/?TriRecherche=Prix-ASC`;
                default:
                  return '';
              }
            },
          };

        case AELink.Summer:
          return {
            href: () => {
              switch (locale) {
                case Locale.Fr:
                  return `${getProdDomain(Locale.Fr)}/maisons-de-vacances-ardennes/vacances-ete`;
                case Locale.Nl:
                  return `${getProdDomain(Locale.Nl)}/vakantiehuizen-ardennen/zomervakantie`;
                case Locale.De:
                  return `${getProdDomain(Locale.De)}/ferienhauser-ardennen/urlaub-sommerferien`;
                case Locale.En:
                  return `${getProdDomain(Locale.En)}/holiday-houses-ardennes/summer-holidays`;
                default:
                  return '';
              }
            },
          };

        case AELink.RomanticWeekend:
          return {
            href: () => {
              switch (locale) {
                case Locale.Fr:
                  return `${getProdDomain(Locale.Fr)}/maisons-de-vacances-ardennes/week-end-en-amoureux`;
                case Locale.Nl:
                  return `${getProdDomain(Locale.Nl)}/vakantiehuizen-ardennen/romantisch-weekendje-weg`;
                case Locale.De:
                  return `${getProdDomain(Locale.De)}/ferienhauser-ardennen/romantikwochenende`;
                case Locale.En:
                  return `${getProdDomain(Locale.En)}/holiday-houses-ardennes/romantic-weekend-breaks`;
                default:
                  return '';
              }
            },
          };

        case AELink.EntertainmentStay:
          return {
            href: () => {
              switch (locale) {
                case Locale.Fr:
                  return `${getProdDomain(Locale.Fr)}/maisons-de-vacances-ardennes/sejour-divertissement`;
                case Locale.Nl:
                  return `${getProdDomain(Locale.Nl)}/vakantiehuizen-ardennen/pretgarantie`;
                case Locale.De:
                  return `${getProdDomain(Locale.De)}/ferienhauser-ardennen/aufenthalt-unterhaltung`;
                case Locale.En:
                  return `${getProdDomain(Locale.En)}/holiday-houses-ardennes/entertainment-stay`;
                default:
                  return '';
              }
            },
          };

        case AELink.Wellness:
          return {
            href: () => {
              switch (locale) {
                case Locale.Fr:
                  return `${getProdDomain(Locale.Fr)}/maisons-de-vacances-ardennes/maisons-de-vacances-ardennes/Vacances-wellness-en-Ardenne`;
                case Locale.Nl:
                  return `${getProdDomain(Locale.Nl)}/vakantiehuizen-ardennen/Wellness-vakantie-in-de-Ardennen`;
                case Locale.De:
                  return `${getProdDomain(Locale.De)}/ferienhauser-ardennen/Wellnesurlaub-in-den-Ardennen`;
                case Locale.En:
                  return `${getProdDomain(Locale.En)}/holiday-houses-ardennes/Wellness-vacation-in-the-Ardennes`;
                default:
                  return '';
              }
            },
          };

        case AELink.Fireplace:
          return {
            href: () => {
              switch (locale) {
                case Locale.Fr:
                  return `${getProdDomain(Locale.Fr)}/recherche/2-60p/Week-end/2024-04-00/Feu-ouvert/?TriRecherche=Satisfaction-DESC`;
                case Locale.Nl:
                  return `${getProdDomain(Locale.Nl)}/search/2-60p/Weekend/2024-04-00/Open-haard/?TriRecherche=Satisfaction-DESC`;
                case Locale.De:
                  return `${getProdDomain(Locale.De)}/suchen/2-60p/Wochenende/2024-04-00/Kamin/?TriRecherche=Satisfaction-DESC`;
                case Locale.En:
                  return `${getProdDomain(Locale.En)}/search/2-60p/Weekend/2024-04-00/Fireplace/?TriRecherche=Satisfaction-DESC`;
                default:
                  return '';
              }
            },
          };
        case AELink.OutdoorPool:
          return {
            href: () => {
              switch (locale) {
                case Locale.Fr:
                  return `${getProdDomain(Locale.Fr)}/maisons-de-vacances-ardennes/piscine-exterieure`;
                case Locale.Nl:
                  return `${getProdDomain(Locale.Nl)}/vakantiehuizen-ardennen/buitenzwembad`;
                case Locale.De:
                  return `${getProdDomain(Locale.De)}/ferienhauser-ardennen/schwimmbad-aussen`;
                case Locale.En:
                  return `${getProdDomain(Locale.En)}/holiday-houses-ardennes/outdoor-pool`;
                default:
                  return '';
              }
            },
          };
        case AELink.HotTub:
          return {
            href: () => {
              switch (locale) {
                case Locale.Fr:
                  return `${getProdDomain(Locale.Fr)}/maisons-de-vacances-ardennes/Vacances-Jacuzzi`;
                case Locale.Nl:
                  return `${getProdDomain(Locale.Nl)}/vakantiehuizen-ardennen/Vakantie-Jacuzzi`;
                case Locale.De:
                  return `${getProdDomain(Locale.De)}/ferienhauser-ardennen/Ferien-Jacuzzi`;
                case Locale.En:
                  return `${getProdDomain(Locale.En)}/holiday-houses-ardennes/Holidays-Jacuzzi`;
                default:
                  return '';
              }
            },
          };

        case AELink.CampagneActionPromo:
          return {
            href: () => {
              switch (locale) {
                case Locale.Fr:
                  return `${getProdDomain(Locale.Fr)}/maisons-de-vacances-ardennes/Vacances-Jacuzzi`;
                case Locale.Nl:
                  return `${getProdDomain(Locale.Nl)}/vakantiehuizen-ardennen/Vakantie-Jacuzzi`;
                case Locale.De:
                  return `${getProdDomain(Locale.De)}/ferienhauser-ardennen/Ferien-Jacuzzi`;
                case Locale.En:
                  return `${getProdDomain(Locale.En)}/holiday-houses-ardennes/Holidays-Jacuzzi`;
                default:
                  return '';
              }
            },
          };
        case AELink.Sauna:
          return {
            href: () => {
              switch (locale) {
                case Locale.Fr:
                  return `${getProdDomain(Locale.Fr)}/maisons-de-vacances-ardennes/Sejour-avec-Sauna`;
                case Locale.Nl:
                  return `${getProdDomain(Locale.Nl)}/vakantiehuizen-ardennen/Verblijven-met-Sauna`;
                case Locale.De:
                  return `${getProdDomain(Locale.De)}/ferienhauser-ardennen/Aufenthalt-mit-Sauna`;
                case Locale.En:
                  return `${getProdDomain(Locale.En)}/holiday-houses-ardennes/Stays-with-Sauna`;
                default:
                  return '';
              }
            },
          };

        case AELink.Inspiration:
          return {
            href: () => {
              switch (locale) {
                case Locale.Fr:
                  return `${getProdDomain(Locale.Fr)}/vacances-idees-et-bons-plans`;
                case Locale.Nl:
                  return `${getProdDomain(Locale.Nl)}/vakantie-ideeen-advies-en-inspiratie`;
                case Locale.De:
                  return `${getProdDomain(Locale.De)}/urlaub-ideen-und-tipps`;
                case Locale.En:
                  return `${getProdDomain(Locale.En)}/holiday-ideas-advice-and-inspiration`;
                default:
                  return '';
              }
            },
          };
        case AELink.RegionsArdennes:
          return {
            href: () => {
              switch (locale) {
                case Locale.Fr:
                  return `${getProdDomain(Locale.Fr)}/experience/article/decouvrez-ces-8-regions-en-ardenne`;
                case Locale.Nl:
                  return `${getProdDomain(Locale.Nl)}/experience/artikel/ontdek-deze-8-zones-de-ardennen`;
                default:
                  return '';
              }
            },
          };

        case AELink.Durbuy:
          return {
            href: () => {
              switch (locale) {
                case Locale.Fr:
                  return `${getProdDomain(Locale.Fr)}/maisons-de-vacances-ardennes/Durbuy`;
                case Locale.Nl:
                  return `${getProdDomain(Locale.Nl)}/vakantiehuizen-ardennen/Durbuy`;
                case Locale.De:
                  return `${getProdDomain(Locale.De)}/ferienhauser-ardennen/Durbuy`;
                case Locale.En:
                  return `${getProdDomain(Locale.En)}/holiday-houses-ardennes/Durbuy`;
                default:
                  return '';
              }
            },
          };
        case AELink.DurbuyThematic:
          return {
            href: () => {
              switch (locale) {
                case Locale.Fr:
                  return `${getProdDomain(Locale.Fr)}/recherche/2-60p/Week-end/0000-00-00/10_Distance/45_maisons-de-vacances-en-Ardenne/?TriRecherche=Satisfaction-DESC`;
                case Locale.Nl:
                  return `${getProdDomain(Locale.Nl)}/zoeken/2-60p/Weekend/0000-00-00/10_Afstand/45_vakantiehuizen-in-de-Ardennen/?TriRecherche=Satisfaction-DESC`;
                case Locale.De:
                  return `${getProdDomain(Locale.De)}/suchen/2-60p/Wochenende/0000-00-00/10_Entfernung/45_ferienwohnungen-in-den-Ardennen/?TriRecherche=Satisfaction-DESC`;
                case Locale.En:
                  return `${getProdDomain(Locale.En)}/search/2-60p/Weekend/0000-00-00/10_Distance/45_holiday-Houses-in-the-Ardennes/?TriRecherche=Satisfaction-DESC `;
                default:
                  return '';
              }
            },
          };
        case AELink.Malmedy:
          return {
            href: () => {
              switch (locale) {
                case Locale.Fr:
                  return `${getProdDomain(Locale.Fr)}/maisons-de-vacances-ardennes/Malmedy`;
                case Locale.Nl:
                  return `${getProdDomain(Locale.Nl)}/vakantiehuizen-ardennen/Malmedy`;
                case Locale.De:
                  return `${getProdDomain(Locale.De)}/ferienhauser-ardennen/Malmedy`;
                case Locale.En:
                  return `${getProdDomain(Locale.En)}/holiday-houses-ardennes/Malmedy`;
                default:
                  return '';
              }
            },
          };
        case AELink.Houffalize:
          return {
            href: () => {
              switch (locale) {
                case Locale.Fr:
                  return `${getProdDomain(Locale.Fr)}/maisons-de-vacances-ardennes/Houffalize`;
                case Locale.Nl:
                  return `${getProdDomain(Locale.Nl)}/vakantiehuizen-ardennen/Houffalize`;
                case Locale.De:
                  return `${getProdDomain(Locale.De)}/ferienhauser-ardennen/Houffalize`;
                case Locale.En:
                  return `${getProdDomain(Locale.En)}/holiday-houses-ardennes/Houffalize`;
                default:
                  return '';
              }
            },
          };
        case AELink.LaRoche:
          return {
            href: () => {
              switch (locale) {
                case Locale.Fr:
                  return `${getProdDomain(Locale.Fr)}/maisons-de-vacances-ardennes/Laroche`;
                case Locale.Nl:
                  return `${getProdDomain(Locale.Nl)}/vakantiehuizen-ardennen/Laroche`;
                case Locale.De:
                  return `${getProdDomain(Locale.De)}/ferienhauser-ardennen/Laroche`;
                case Locale.En:
                  return `${getProdDomain(Locale.En)}/holiday-houses-ardennes/Laroche`;
                default:
                  return '';
              }
            },
          };
        case AELink.EauDheure:
          return {
            href: () => {
              switch (locale) {
                case Locale.Fr:
                  return `${getProdDomain(Locale.Fr)}/maisons-de-vacances-ardennes/Froidchapelle`;
                case Locale.Nl:
                  return `${getProdDomain(Locale.Nl)}/vakantiehuizen-ardennen/Froidchapelle`;
                case Locale.De:
                  return `${getProdDomain(Locale.De)}/ferienhauser-ardennen/Froidchapelle`;
                case Locale.En:
                  return `${getProdDomain(Locale.En)}/holiday-houses-ardennes/Froidchapelle`;
                default:
                  return '';
              }
            },
          };
        case AELink.Dinant:
          return {
            href: () => {
              switch (locale) {
                case Locale.Fr:
                  return `${getProdDomain(Locale.Fr)}/maisons-de-vacances-ardennes/Dinant`;
                case Locale.Nl:
                  return `${getProdDomain(Locale.Nl)}/vakantiehuizen-ardennen/Dinant`;
                case Locale.De:
                  return `${getProdDomain(Locale.De)}/ferienhauser-ardennen/Dinant`;
                case Locale.En:
                  return `${getProdDomain(Locale.En)}/holiday-houses-ardennes/Dinant`;
                default:
                  return '';
              }
            },
          };
        case AELink.Namur:
          return {
            href: () => {
              switch (locale) {
                case Locale.Fr:
                  return `${getProdDomain(Locale.Fr)}/maisons-de-vacances-ardennes/Namur`;
                case Locale.Nl:
                  return `${getProdDomain(Locale.Nl)}/vakantiehuizen-ardennen/Namen`;
                case Locale.De:
                  return `${getProdDomain(Locale.De)}/ferienhauser-ardennen/Namur`;
                case Locale.En:
                  return `${getProdDomain(Locale.En)}/holiday-houses-ardennes/Namur`;
                default:
                  return '';
              }
            },
          };
        case AELink.Bouillon:
          return {
            href: () => {
              switch (locale) {
                case Locale.Fr:
                  return `${getProdDomain(Locale.Fr)}/maisons-de-vacances-ardennes/Bouillon`;
                case Locale.Nl:
                  return `${getProdDomain(Locale.Nl)}/vakantiehuizen-ardennen/Bouillon`;
                case Locale.De:
                  return `${getProdDomain(Locale.De)}/ferienhauser-ardennen/Bouillon`;
                case Locale.En:
                  return `${getProdDomain(Locale.En)}/holiday-houses-ardennes/Bouillon`;
                default:
                  return '';
              }
            },
          };
        case AELink.Spa:
          return {
            href: () => {
              switch (locale) {
                case Locale.Fr:
                  return `${getProdDomain(Locale.Fr)}/maisons-de-vacances-ardennes/Spa`;
                case Locale.Nl:
                  return `${getProdDomain(Locale.Nl)}/vakantiehuizen-ardennen/Spa`;
                case Locale.De:
                  return `${getProdDomain(Locale.De)}/ferienhauser-ardennen/Spa`;
                case Locale.En:
                  return `${getProdDomain(Locale.En)}/holiday-houses-ardennes/Spa`;
                default:
                  return '';
              }
            },
          };

        case AELink.TerraceBreathtakingView:
          return {
            href: () => {
              switch (locale) {
                case Locale.Fr:
                  return `${getProdDomain(Locale.Fr)}/maisons-de-vacances-ardennes/terrasse-vue-imprenable`;
                case Locale.Nl:
                  return `${getProdDomain(Locale.Nl)}/vakantiehuizen-ardennen/terras-met-uniek-uitzicht`;
                case Locale.De:
                  return `${getProdDomain(Locale.De)}/ferienhauser-ardennen/terrasse-blick`;
                case Locale.En:
                  return `${getProdDomain(Locale.En)}/holiday-houses-ardennes/terrace-breathtaking-view`;
                default:
                  return '';
              }
            },
          };

        case AELink.MayCampagne:
          return {
            href: () => {
              switch (locale) {
                case Locale.Fr:
                  return `${getProdDomain(Locale.Fr)}/recherche/8-20p/Week-end/2024-05-00/4-30_Chambres/2-30_Salle_de_bain/3_Confort/barbecue/jardin-prive/?TriRecherche=Satisfaction-DESC`;
                case Locale.Nl:
                  return `${getProdDomain(Locale.Nl)}/zoeken/8-20p/Weekend/2024-05-00/4-30_Kamers/2-30_Badkamer/3_Comfort/barbecue/privetuin?TriRecherche=Satisfaction-DESC`;
                case Locale.De:
                  return `${getProdDomain(Locale.De)}/suchen/8-20p/Wochenende/2024-05-00/4-30_Zimmer/2-30_Badezimmer/3_Komfort/grill/privater-garten/?TriRecherche=Satisfaction-DESC`;
                case Locale.En:
                  return `${getProdDomain(Locale.En)}/search/8-20p/Weekend/2024-05-00/4-30_Rooms/2-30_Bathroom/3_Comfort/barbecue/private-garden/?TriRecherche=Satisfaction-DESC`;
                default:
                  return '';
              }
            },
          };

        case AELink.InsuranceDamage:
          return {
            href: () =>
              `${prodDomain}/help-center/2-ma-reservation/114-que-faire-en-cas-de-degats`,
            title: t('footer.insurance'),
            alt: t('footer.insurance'),
          };
        case AELink.Owner:
          return {
            href: () => {
              switch (locale) {
                case Locale.Nl:
                  return `${getProdDomain(Locale.Nl)}/proprietaires/nl/index.do`;
                case Locale.Fr:
                case Locale.De:
                case Locale.En:
                default:
                  return `${getProdDomain(Locale.Fr)}/proprietaires/fr/index.do`;
              }
            },
            title: t('footer.my_host_account'),
            alt: t('footer.my_host_account'),
          };
        case AELink.GeneralRegulation:
          return {
            href: () => {
              switch (locale) {
                case Locale.Fr:
                  return `${getProdDomain(Locale.Fr)}/ressource/pdf/AE_2013-08_ReglementOrdreINT.pdf`;
                case Locale.Nl:
                  return `${getProdDomain(Locale.Nl)}/ressource/pdf/AE_2013-08_ReglementOrdreINT_NL.pdf`;
                case Locale.De:
                  return `${getProdDomain(Locale.De)}/ressource/pdf/AE_2013-08_ReglementOrdreINT_DE.pdf`;
                case Locale.En:
                default:
                  return `${getProdDomain(Locale.En)}/ressource/pdf/AE_2013-08_ReglementOrdreINT_EN.pdf`;
              }
            },
          };
        case AELink.Search:
          return {
            href: () => {
              switch (locale) {
                case Locale.Fr:
                  return `/recherche`;
                case Locale.Nl:
                  return `/zoeken`;
                case Locale.De:
                  return `/suchen`;
                case Locale.En:
                default:
                  return `/search`;
              }
            },
          };
        case AELink.VoucherFaq:
          return {
            href: () => {
              switch (locale) {
                case Locale.Fr:
                  return `${getProdDomain(Locale.Fr)}/help-center/4-covid-19/49-comment-utiliser-votre-bon-a-valoir`;
                case Locale.Nl:
                  return `${getProdDomain(Locale.Nl)}/help-center/4-covid-19/49-hoe-dient-u-te-boeken-met-een-tegoedbon`;
                case Locale.De:
                  return `${getProdDomain(Locale.De)}/help-center/4-covid/49-wie-losen-sie-ihren-gutschein-ei`;
                case Locale.En:
                default:
                  return `${getProdDomain(Locale.En)}/help-center/4-covid-19/49-how-to-use-your-voucher`;
              }
            },
          };
        case AELink.VoucherOrder:
          return {
            href: () => {
              switch (locale) {
                case Locale.Fr:
                  return `${getProdDomain(Locale.Fr)}/cheque-cadeau`;
                case Locale.Nl:
                  return `${getProdDomain(Locale.Nl)}/cadeaubon-bestellen`;
                case Locale.De:
                  return `${getProdDomain(Locale.De)}/geschenkgutschein-bestellen`;
                case Locale.En:
                default:
                  return `${getProdDomain(Locale.En)}/gift-vouchers`;
              }
            },
          };
        case AELink.PrivacyPolicy:
          return {
            href: `${prodDomain}/privacy-policy?p=ViePrivee`,
            title: t('footer.privacy_policy'),
            alt: t('footer.privacy_policy'),
          };
        case AELink.Disclaimer:
          return {
            href: `${prodDomain}/disclaimer`,
            title: t('footer.disclaimer'),
            alt: t('footer.disclaimer'),
          };
        default:
          throw Error('link not found');
      }
    },
    [authenticated, bookings, dayjs, locale, prodDomain, t]
  );

  return useLink<AELink>(getLink);
};
